import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Button, ModalLabel, Textarea } from '../../GlobalStyles';
import { PCARCommentInfo, AgencyComment } from './DataReview.interfaces';
import useApi from '../api/axiosAdapter';

interface CommentEditModalProps {
    isOpen: boolean;
    onClose: () => void;
    record: PCARCommentInfo;
    currentUserId: number;
    isPM: boolean;
    isSWCANonPM: boolean;
    isAgencyUser: boolean;
    onSave: (updatedRecord: PCARCommentInfo) => void;
    onDataChange?: () => void;
}

const CommentEditModal: React.FC<CommentEditModalProps> = ({
    isOpen,
    onClose,
    record,
    currentUserId,
    isPM,
    isSWCANonPM,
    isAgencyUser,
    onSave,
    onDataChange,
}) => {
    const [response, setResponse] = useState('');
    const [techEdit, setTechEdit] = useState('');
    const [agencyReview, setAgencyReview] = useState('');
    const [pmNotes, setPmNotes] = useState('');
    const [agencyComments, setAgencyComments] = useState<AgencyComment[]>([]);
    const [newCommentText, setNewCommentText] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [statusMessage, setStatusMessage] = useState('');
    const [statusType, setStatusType] = useState<'success' | 'error'>('success');

    const { post } = useApi();

    // When PM approval is checked, lock the entire modal (PM approval itself is managed in-table)
    const isLocked = record?.pm_complete ?? false;
    const isSWCAUser = isPM || isSWCANonPM;

    // Initialize form fields when modal opens
    useEffect(() => {
        if (isOpen && record) {
            setResponse(record.response || '');
            setTechEdit(record.tech_edit || '');

            setPmNotes(record.pm_notes || '');
            setAgencyComments(Array.isArray(record.agency_comments) ? record.agency_comments : []);
            setNewCommentText('');
            setStatusMessage('');
        }
    }, [isOpen, record]);

    // Handle adding a new agency comment
    const handleAddComment = async () => {
        if (!newCommentText.trim()) {
            setStatusMessage('Please enter a comment');
            setStatusType('error');
            return;
        }

        try {
            const url = `pcar/agency_comment_add?comment_uuid=${record.comment_uuid}&comment_text=${encodeURIComponent(newCommentText)}&user_id=${currentUserId}`;
            const response = await post<{ status: string; message: string }>(url);
            
            if (response && response.data) {
                if (response.data.status === 'success') {
                    setNewCommentText('');
                    setStatusMessage(response.data.message || 'Comment added successfully');
                    setStatusType('success');
                    
                    // Trigger data refresh in parent component to get updated comments
                    if (onDataChange) {
                        onDataChange();
                    }
                } else {
                    setStatusMessage(response.data.message || 'Error adding comment');
                    setStatusType('error');
                }
            }
        } catch (error) {
            console.error('Error adding agency comment:', error);
            setStatusMessage('Error adding comment: ' + error);
            setStatusType('error');
        }
    };

    // Handle deleting an agency comment (soft delete - mark as deleted)
    const handleDeleteComment = async (agency_review_uuid: string) => {
        try {
            const url = `pcar/agency_comment_delete?agency_review_uuid=${agency_review_uuid}`;
            const response = await post<{ status: string; message: string }>(url);
            
            if (response && response.data) {
                if (response.data.status === 'success') {
                    setStatusMessage(response.data.message || 'Comment deleted successfully');
                    setStatusType('success');
                    
                    // Trigger data refresh in parent component to get updated comments
                    if (onDataChange) {
                        onDataChange();
                    }
                } else {
                    setStatusMessage(response.data.message || 'Error deleting comment');
                    setStatusType('error');
                }
            }
        } catch (error) {
            console.error('Error deleting agency comment:', error);
            setStatusMessage('Error deleting comment: ' + error);
            setStatusType('error');
        }
    };

    // Handle saving all text fields
    const handleSave = async () => {
        setIsSaving(true);
        setStatusMessage('');

        try {
            // Build updates object based on what this user can edit
            const updates: Record<string, string> = {};
            if (isSWCAUser) {
                updates.response = response;
                updates.tech_edit = techEdit;
            }
            if (isPM) {
                updates.pm_notes = pmNotes;
            }

            const url = `pcar/update_response`;
            const payload = {
                comment_uuid: record.comment_uuid,
                updates,
            };

            await post(url, payload);

            const updatedRecord: PCARCommentInfo = {
                ...record,
                ...(isSWCAUser ? { response, tech_edit: techEdit } : {}),
                ...(isPM ? { pm_notes: pmNotes } : {}),
                agency_comments: agencyComments,
            };

            setStatusMessage('Changes saved successfully');
            setStatusType('success');
            
            onSave(updatedRecord);
            
            setTimeout(() => {
                onClose();
            }, 1000);
        } catch (error) {
            console.error('Error saving:', error);
            setStatusMessage('Error saving changes: ' + error);
            setStatusType('error');
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <Overlay onClick={onClose}>
            <ModalContainer onClick={(e) => e.stopPropagation()}>
                <ModalHeader>
                    <ModalTitle>Edit Comment #{record.letter_comment_num}</ModalTitle>
                    <CloseButton onClick={onClose}>&times;</CloseButton>
                </ModalHeader>

                <ModalBody>
                    {statusMessage && (
                        <StatusMessage type={statusType}>
                            {statusMessage}
                        </StatusMessage>
                    )}

                    {/* Read-only comment text for context */}
                    <Section>
                        <ModalLabel>Original Comment (Read-Only)</ModalLabel>
                        <ReadOnlyText>{record.comment_text}</ReadOnlyText>
                    </Section>

                    <Section>
                        <ModalLabel>Comment Code (Read-Only)</ModalLabel>
                        <ReadOnlyText>{record.comment_code}</ReadOnlyText>
                    </Section>

                    {/* Editable text fields */}
                    <Section>
                        <ModalLabel>Response</ModalLabel>
                        <Textarea
                            value={response}
                            onChange={(e) => setResponse(e.target.value)}
                            placeholder="Enter response..."
                            disabled={isSaving || isAgencyUser || isLocked}
                        />
                    </Section>

                    <Section>
                        <ModalLabel>Document Edit</ModalLabel>
                        <Textarea
                            value={techEdit}
                            onChange={(e) => setTechEdit(e.target.value)}
                            placeholder="Enter document edit notes..."
                            disabled={isSaving || isAgencyUser || isLocked}
                        />
                    </Section>

                    {/* PM Notes: hidden from agency users; read-only for SWCA non-PM */}
                    {!isAgencyUser && (
                        <Section>
                            <ModalLabel>PM Notes</ModalLabel>
                            <Textarea
                                value={pmNotes}
                                onChange={(e) => setPmNotes(e.target.value)}
                                placeholder="Enter PM notes..."
                                disabled={isSaving || isSWCANonPM || isLocked}
                            />
                        </Section>
                    )}

                    {/* Save and Cancel buttons — agency users have no editable text fields */}
                    {!isAgencyUser && (
                        <ButtonRow>
                            <Button variant="secondary" onClick={onClose} disabled={isSaving}>
                                Cancel
                            </Button>
                            <Button onClick={handleSave} disabled={isSaving || isLocked}>
                                {isSaving ? 'Saving...' : 'Save Changes'}
                            </Button>
                        </ButtonRow>
                    )}

                    {/* Agency Review Comments Section */}
                    {/* Rules:
                        - Agency users: can add new comments, can delete/edit their own
                        - PMs: can delete any comment (cannot add or edit)
                        - SWCA non-PM: view only
                        - Everything locked when pm_complete is true
                    */}
                    <Section>
                        <ModalLabel>Agency Review Comments</ModalLabel>
                        
                        {/* Existing comments */}
                        {agencyComments.length > 0 ? (
                            <CommentsList>
                                {agencyComments.map((ac) => (
                                    <CommentItem key={ac.agency_review_uuid}>
                                        <CommentText>
                                            {ac.comment_text} <CommentAuthor>({ac.name})</CommentAuthor>
                                        </CommentText>
                                        {/* Agency user: can delete own comments; PM: can delete any */}
                                        {!isLocked && (isAgencyUser
                                            ? ac.user_id == currentUserId
                                            : isPM
                                        ) && (
                                            <DeleteButton 
                                                onClick={() => handleDeleteComment(ac.agency_review_uuid)}
                                                title="Delete comment"
                                            >
                                                ×
                                            </DeleteButton>
                                        )}
                                    </CommentItem>
                                ))}
                            </CommentsList>
                        ) : (
                            <NoComments>No agency comments yet</NoComments>
                        )}

                        {/* Add new comment — only agency users can add, and only when not locked */}
                        {isAgencyUser && !isLocked && (
                            <AddCommentSection>
                                <Textarea
                                    value={newCommentText}
                                    onChange={(e) => setNewCommentText(e.target.value)}
                                    placeholder="Add a new agency comment..."
                                    style={{ minHeight: '60px', flex: 1 }}
                                />
                                <Button 
                                    onClick={handleAddComment}
                                    style={{ marginLeft: '8px', alignSelf: 'flex-start' }}
                                >
                                    Add Comment
                                </Button>
                            </AddCommentSection>
                        )}
                    </Section>
                </ModalBody>
            </ModalContainer>
        </Overlay>
    );
};

// Styled Components
const Overlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
`;

const ModalContainer = styled.div`
    background: white;
    border-radius: 8px;
    width: 90%;
    max-width: 900px;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const ModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    border-bottom: 1px solid #e0e0e0;
`;

const ModalTitle = styled.h2`
    margin: 0;
    font-size: 24px;
    color: #333;
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    font-size: 32px;
    cursor: pointer;
    color: #666;
    padding: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
        color: #333;
    }
`;

const ModalBody = styled.div`
    padding: 20px;
    overflow-y: auto;
    flex: 1;
`;

const ButtonRow = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding: 20px 0;
    margin-top: 10px;
    border-bottom: 2px solid #e0e0e0;
    margin-bottom: 20px;
`;

const Section = styled.div`
    margin-bottom: 20px;
`;

const ReadOnlyText = styled.div`
    padding: 12px;
    background-color: #f5f5f5;
    border: 1px solid #ddd;
    border-radius: 4px;
    color: #666;
    font-size: 14px;
    line-height: 1.5;
`;

const StatusMessage = styled.div<{ type: 'success' | 'error' }>`
    padding: 12px;
    margin-bottom: 16px;
    border-radius: 4px;
    background-color: ${props => props.type === 'success' ? '#d4edda' : '#f8d7da'};
    color: ${props => props.type === 'success' ? '#155724' : '#721c24'};
    border: 1px solid ${props => props.type === 'success' ? '#c3e6cb' : '#f5c6cb'};
`;

const CommentsList = styled.div`
    margin-bottom: 16px;
`;

const CommentItem = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 10px;
    margin-bottom: 8px;
    background-color: #f9f9f9;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
`;

const CommentText = styled.div`
    flex: 1;
    font-size: 14px;
    line-height: 1.5;
`;

const CommentAuthor = styled.span`
    color: #666;
    font-style: italic;
`;

const DeleteButton = styled.button`
    background: none;
    border: none;
    color: #dc3545;
    font-size: 24px;
    cursor: pointer;
    padding: 0 8px;
    line-height: 1;
    
    &:hover {
        color: #c82333;
    }
`;

const NoComments = styled.div`
    padding: 20px;
    text-align: center;
    color: #999;
    font-style: italic;
`;

const AddCommentSection = styled.div`
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #e0e0e0;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
`;

export default CommentEditModal;
